import type { AuthSession, SignupResponse, ChallengeResponse } from '~/types/domain'

// /auth/login now returns the full actor envelope (same shape as /me and
// /auth/refresh), so callers can applySession in one round trip.
type LoginResult = AuthSession | ChallengeResponse

interface SignupData {
  email: string
  password: string
  givenName: string
  familyName: string
  nickname?: string
}

export function useAuth() {
  const { $api } = useNuxtApp()

  async function login(email: string, password: string, remember: boolean): Promise<LoginResult> {
    return $api<LoginResult>('/auth/login', {
      method: 'POST',
      body: { email, password, remember }
    })
  }

  async function signup(data: SignupData): Promise<SignupResponse> {
    return $api<SignupResponse>('/auth/signup', { method: 'POST', body: data })
  }

  async function confirmSignup(email: string, code: string): Promise<AuthSession> {
    return $api<AuthSession>('/auth/confirm-signup', { method: 'POST', body: { email, code } })
  }

  async function logout(): Promise<void> {
    await $api('/auth/logout', { method: 'POST' })
  }

  async function fetchMe(): Promise<AuthSession | null> {
    try {
      return await $api<AuthSession>('/me')
    } catch {
      return null
    }
  }

  async function refreshSession(): Promise<AuthSession | null> {
    try {
      return await $api<AuthSession>('/auth/refresh', { method: 'POST' })
    } catch {
      return null
    }
  }

  async function checkSession(): Promise<boolean> {
    try {
      const res = await $api<{ authenticated: boolean }>('/auth/session')
      return res.authenticated
    } catch {
      return false
    }
  }

  async function exchangeOauthCode(code: string, codeVerifier: string, redirectUri: string): Promise<void> {
    await $api('/auth/callback', {
      method: 'POST',
      body: { code, codeVerifier, redirectUri }
    })
  }

  async function forgotPassword(email: string): Promise<void> {
    await $api('/auth/forgot-password', { method: 'POST', body: { email } })
  }

  async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await $api('/auth/confirm-forgot-password', { method: 'POST', body: { email, code, newPassword } })
  }

  async function resendConfirmation(email: string): Promise<void> {
    await $api('/auth/resend-confirmation', { method: 'POST', body: { email } })
  }

  async function startAssume(userId: string, reason: string): Promise<AuthSession> {
    return $api<AuthSession>(`/admin/users/${userId}/assume`, { method: 'POST', body: { reason } })
  }

  async function stopAssume(): Promise<void> {
    await $api('/auth/assume/stop', { method: 'POST' })
  }

  return {
    login,
    signup,
    confirmSignup,
    logout,
    fetchMe,
    refreshSession,
    checkSession,
    exchangeOauthCode,
    forgotPassword,
    confirmForgotPassword,
    resendConfirmation,
    startAssume,
    stopAssume
  }
}
